import * as React from 'react';

// eslint-disable-next-line no-restricted-imports -- required for AEM Universal Editor compatibility
import styled, { createGlobalStyle } from 'styled-components';
import type { UnifiedThemeType } from '@oneaudi/unified-web-common';
import { SYS_BREAKPOINT_MD, ThemeProvider } from '@oneaudi/unified-web-common';

import type { StageProps } from '../../../types';
import { AssetTypeEnum, Mode, CTAsTheme, ContentPosition } from '../../../types';

import StageLegal, { StageLegalWrapper } from '../stage-legal/StageLegal';
import StageButtons, { ResponsiveButtonsWrapper } from '../stage-content/StageButtons';
import { useTracking } from '../../hooks/useTracking';

import StageVideo from '../stage-video/StageVideo';
import StageImage from '../stage-image/StageImage';
import StageShader from '../stage-shader/StageShader';
import StageContent, { HeadlineSubLineWrapper } from '../stage-content/StageContent';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ContentContext } from '../../ContentContext';
import { FascinationContext } from '../../context/FascinationContext';
import { getBackgroundColor, getThemeName } from '../../utils/alphaHelper';

// this is needed to combat the infitine image height increasing problem in universal editor
// eslint-disable-next-line no-restricted-syntax -- required for AEM Universal Editor compatibility
const CustomGlobalStyle = createGlobalStyle`
  html.adobe-ue-edit .mediaItem {
    height: 800px;
  }
    #fa-editorial-stage-video_fa-video-player-videoAppMuteButtonWrapper {
      z-index: 5;
    }

    a.audi-j-footnote-reference {
      color: inherit;
      text-decoration: none;
    }
`;

const StageContainerWrap = styled.div<{ appTheme: CTAsTheme }>`
  background-color: ${({ appTheme }) => getBackgroundColor(appTheme)};
  margin: 0;
  position: relative;
`;

const Block = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: var(--max-content-width);

  & a.audi-j-footnote-reference:hover {
    color: inherit;
  }
`;

interface ShowOnMobileProps {
  isVideoStage: boolean;
}
const ShowOnMobile = styled.div<ShowOnMobileProps>`
  display: none;
  padding: 0 var(--page-margin) var(--spacing-relative-lg) var(--page-margin);

  @media (max-width: ${SYS_BREAKPOINT_MD}) {
    display: ${({ isVideoStage }) => (isVideoStage ? 'block' : 'none')};
  }

  ${ResponsiveButtonsWrapper} {
    padding: 0;
  }
`;

interface ShowOnDesktopProps {
  isVideoStage: boolean;
}
const ShowOnDesktop = styled.div<ShowOnDesktopProps>`
  display: block;

  @media (max-width: ${SYS_BREAKPOINT_MD}) {
    display: none;
    display: ${({ isVideoStage }) => (isVideoStage ? 'none' : 'block')};
  }

  ${StageLegalWrapper} {
    padding-left: 0;
    padding-bottom: 0;
  }
`;

export type StageContainerContent = StageProps;

const StageContainer: React.FC = () => {
  const { content, isHeadless } = React.useContext(ContentContext);
  const {
    asset,
    appearance,
    text,
    buttons,
    consumptionAndEmissions,
    disclaimers,
    prices,
    appearancemodelPage,
  } = content;

  const showPrices = isHeadless && !!(prices?.totalPrice || prices?.monthlyRate);
  const isModelPage = isHeadless && (showPrices || appearancemodelPage);

  const { mode, theme, backgroundDisclaimersCTAs } = appearance;
  const ref = React.useRef<HTMLDivElement>(null);
  const mobileObserverRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const tracking = useTracking(ref, content, {
    implementer: 37,
  });
  const { registerRef, unregisterRef } = React.useContext(FascinationContext);

  React.useEffect(() => {
    tracking.sendReadyEvent();
    tracking.registerImpressionTracking();
  }, []);
  const isVideoStage = asset && asset.assetType === AssetTypeEnum.Video;

  const hideControls = isVideoStage
    ? isMobile
      ? (asset.hideNativeControlsPortrait ?? false)
      : (asset.hideNativeControlsLandscape ?? false)
    : false;

  const backgroundTheme = (() => {
    if (backgroundDisclaimersCTAs !== CTAsTheme.Media && !isModelPage) {
      return backgroundDisclaimersCTAs;
    }
    return theme;
  })();

  const isLegalVisible = (() => {
    if (appearance.contentPosition !== ContentPosition.BOTTOM_LEFT && !isModelPage) {
      return true;
    }
    if (isMobile || (appearance.backgroundDisclaimersCTAs !== CTAsTheme.Media && !isModelPage)) {
      return true;
    }
    return false;
  })();

  const overwrittenThemeName = getThemeName(theme.toLowerCase() as UnifiedThemeType['name']);

  React.useEffect(() => {
    if (!isMobile || !isVideoStage) {
      return;
    }

    const element = mobileObserverRef.current;

    if (element) {
      registerRef(element);
    }

    return () => {
      unregisterRef();
    };
  }, [isMobile, isVideoStage, registerRef, unregisterRef]);

  return (
    <ThemeProvider theme={{ name: overwrittenThemeName }}>
      <CustomGlobalStyle />
      <StageContainerWrap ref={ref} appTheme={backgroundTheme as CTAsTheme}>
        {asset ? (
          <Block>
            <ShowOnDesktop isVideoStage={isVideoStage}>
              <StageShader
                isModelPage={isModelPage}
                isVideoStage={isVideoStage}
                appearance={appearance}
              >
                <StageContent
                  appearance={appearance}
                  text={text}
                  mode={mode}
                  consumptionAndEmissions={consumptionAndEmissions}
                  buttons={isMobile && mode === Mode.SMALL && !isModelPage ? undefined : buttons}
                  tracking={tracking}
                  isMobile={isMobile}
                  disclaimers={disclaimers}
                  showPrices={showPrices}
                  isModelPage={isModelPage}
                  isVideoStage={isVideoStage}
                  hideControls={hideControls}
                />
              </StageShader>
            </ShowOnDesktop>
            {isVideoStage && (
              <StageVideo
                mode={isModelPage ? Mode.LARGE : mode}
                asset={asset}
                data-testid="stage-video"
              />
            )}
            {!isVideoStage && <StageImage mode={isModelPage ? Mode.LARGE : mode} asset={asset} />}
          </Block>
        ) : null}
        <Block>
          {isMobile && isVideoStage && (
            <ShowOnMobile ref={mobileObserverRef} isVideoStage={isVideoStage}>
              <HeadlineSubLineWrapper
                appearance={appearance}
                tracking={tracking}
                headline={text.headline}
                headingTag={text.headingTag || 'h1'}
                subline={text.subline}
                showSubline={text.subline && !showPrices}
                isModelPage={isModelPage}
                isVideoStage={isVideoStage}
                showPrices={showPrices}
              />
              <StageButtons
                buttons={buttons}
                tracking={tracking}
                isMobile={isMobile}
                isVideoStage={isVideoStage}
                data-testid="stage-buttons"
                displayOnMobile={true}
              />
            </ShowOnMobile>
          )}
          {isMobile && mode === Mode.SMALL && !isModelPage && !isVideoStage && (
            <StageButtons
              buttons={buttons}
              tracking={tracking}
              isMobile={isMobile}
              data-testid="stage-buttons"
              displayOnMobile={true}
            />
          )}
          {isLegalVisible && (
            <StageLegal
              consumptionAndEmissions={consumptionAndEmissions}
              disclaimers={disclaimers}
              appearance={appearance}
              isModelPage={isModelPage}
              isVideoStage={isVideoStage}
              data-testid="stage-legal"
            />
          )}
        </Block>
      </StageContainerWrap>
    </ThemeProvider>
  );
};

export default StageContainer;
